import { useCallback, useEffect, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { toast } from 'sonner';

/** One processing step inside a preset (same shape as flow steps). */
export interface PresetStep {
    name: string;
    arguments: { [key: string]: string };
    argumentTypes?: { [key: string]: string | string[] };
    fileType: string;
    fileCount: number;
    saveOutput: boolean;
    title?: string;
    description?: string;
}

/** An org-scoped, named, multi-step flow snippet synced from BoothLab/booths. */
export interface ProcessingPreset {
    id: number;
    orgId: number;
    name: string;
    description: string | null;
    steps: PresetStep[];
    updatedAt: string;
    createdAt: string;
}

export default function useProcessingPresets() {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();

    const [presets, setPresets] = useState<ProcessingPreset[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!orgId) return;
        setIsLoading(true);
        try {
            const response = await authenticatedApi.get(
                `/${orgId}/processing-presets`,
            );
            setPresets(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error loading processing presets:', error);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const savePreset = useCallback(
        async (name: string, steps: PresetStep[], description?: string) => {
            if (!orgId) return null;
            try {
                const response = await authenticatedApi.post(
                    `/${orgId}/processing-presets`,
                    { name, description: description ?? null, steps },
                );
                toast.success(`Preset "${name}" saved`);
                await refresh();
                return response.data as ProcessingPreset;
            } catch (error) {
                console.error('Error saving processing preset:', error);
                toast.error('Failed to save preset');
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, refresh],
    );

    const deletePreset = useCallback(
        async (presetId: number) => {
            if (!orgId) return;
            try {
                await authenticatedApi.delete(
                    `/${orgId}/processing-presets/${presetId}`,
                );
                toast.success('Preset deleted');
                await refresh();
            } catch (error) {
                console.error('Error deleting processing preset:', error);
                toast.error('Failed to delete preset');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, refresh],
    );

    return { presets, isLoading, refresh, savePreset, deletePreset };
}
